import { useEffect, useRef, useState } from 'react';

import { MeterBarTooltipItem } from './MeterBarTooltipItem/MeterBarTooltipItem.js';
import { useChartActionsMenu } from '../../../../core/components/series-action/hooks/useChartActionsMenu.js';
import { mergeTooltipActions } from '../../../../core/components/series-action/utils/mergeTooltipActions.js';
import { ChartTooltip } from '../../../../core/components/tooltip/ChartTooltip/ChartTooltip.js';
import { TooltipBody } from '../../../../core/components/tooltip/section-components/TooltipBody.js';
import { useTooltipSeriesActions } from '../../../../core/components/tooltip/shared/hooks/useTooltipSeriesActions.js';
import type { Point } from '../../../../core/types/point.js';
import { TooltipItem } from '../../../../core/types/tooltip.js';
import {
  useTooltipPinned,
  useTooltipPosition,
  useTooltipVisible,
} from '../../hooks/store/selectors.js';
import { useTooltipEventListeners } from '../../hooks/use-tooltip-event-listeners.js';

interface MeterBarTooltipProps {
  items: TooltipItem[];
}

// TODO: check if it's still needed once decoupling of the mouse tracking story is done (APPDEV-7070)
function isInitialPosition(mousePosition: Point) {
  const { x, y } = mousePosition;
  return x === 0 && y === 0;
}

const ESTIMATED_ITEM_HEIGHT_PX = 22;

export const MeterBarTooltip = (props: MeterBarTooltipProps) => {
  const { items } = props;
  const [tooltipItems, setTooltipItems] = useState<TooltipItem[]>([]);

  const tooltipRef = useRef<HTMLDivElement>(null);

  const isPinned = useTooltipPinned();
  const position = useTooltipPosition();
  const isVisible = useTooltipVisible();

  useTooltipEventListeners(tooltipRef);

  const {
    onLeave: handleMenuLeave,
    selectedItem: selectedItemId,
    updateSelectedItem,
    actionsMenuRef,
  } = useChartActionsMenu();

  const seriesActions = useTooltipSeriesActions(items, (item) => item.id);

  useEffect(() => {
    if (!isPinned) {
      setTooltipItems(items);
    }
  }, [isPinned, items]);

  if (isInitialPosition(position)) {
    return null;
  }

  if (!isVisible) {
    return null;
  }

  return (
    <ChartTooltip
      ref={tooltipRef}
      pinned={isPinned}
      position={position}
      onMouseLeave={handleMenuLeave}
      actionSelectionValue={{
        selectedItemId,
        actionsMenuRef,
        updateSelectedItem,
      }}
    >
      <TooltipBody
        style={{
          minHeight: `${ESTIMATED_ITEM_HEIGHT_PX}px`,
        }}
        virtualization={false}
      >
        {tooltipItems.map((item, index) => (
          <MeterBarTooltipItem
            key={item.id}
            item={item}
            showDivider={index !== tooltipItems.length - 1}
            actions={mergeTooltipActions(item, seriesActions)}
          />
        ))}
      </TooltipBody>
    </ChartTooltip>
  );
};

(
  MeterBarTooltip as typeof MeterBarTooltip & { displayName: string }
).displayName = 'MeterBarTooltip';
